import { Button, Muted, Stack, color, space, status } from '@biopeak/ui';
import {
  DocumentAnalysisError,
  useLatestDocumentAnalysis,
  type DocumentAnalysisContent,
  type DocumentAnalysisResult,
  type StoredDocumentAnalysis,
} from '../lib/documentAnalysis.ts';

/**
 * Análise por IA dos analitos de UM exame.
 *
 * Aparece DENTRO do card do exame, e não numa aba própria: a pergunta que ela
 * responde ("o que este laudo diz junto?") só faz sentido ao lado do laudo. É a
 * diferença desta feature para a "Análise do período" da Visão geral, que olha
 * o vestível e não toca em exame.
 *
 * DUAS REGRAS DE INTERFACE QUE NÃO SÃO NEGOCIÁVEIS AQUI, herdadas de
 * `PatientAnalysisCard` e válidas pelo mesmo motivo:
 *
 * 1. Nunca exibir a análise sem dizer que foi gerada por IA, sobre quantos
 *    valores, e quando. Texto clínico sem procedência é indistinguível de
 *    laudo, e não é isso que ele é.
 * 2. Achado não verificado aparece VISUALMENTE distinto. A promessa é que todo
 *    número veio do Postgres; esconder quando o modelo cita um número fora do
 *    brief transformaria a promessa em mentira.
 *
 * A MUTAÇÃO VEM DE FORA, por props, e isto não é preferência de estilo: o botão
 * "Análise da IA" mora no cabeçalho do card do exame, junto de "Ver lado a
 * lado" e "Abrir em nova aba". `useMutation` NÃO compartilha estado entre
 * instâncias do hook (ao contrário de `useQuery`, que deduplica por chave) —
 * chamar `useGenerateDocumentAnalysis` aqui dentro criaria uma segunda mutação
 * que nunca saberia do clique lá em cima, e o componente ficaria eternamente
 * mudo. Uma instância só, no dono do botão.
 */
export function DocumentAnalysis({
  documentId,
  isPending,
  error,
  result,
  onRefazer,
}: {
  documentId: string;
  isPending: boolean;
  error: unknown;
  result?: DocumentAnalysisResult;
  onRefazer: () => void;
}) {
  const guardada = useLatestDocumentAnalysis(documentId);

  const analise = guardada.data;
  const erro = error instanceof DocumentAnalysisError ? error : null;
  const semDado = result?.status === 'sem_dado';

  // Nada gerado, nada pedido, nada errado: o card não ocupa espaço no exame.
  if (!analise && !erro && !semDado && !isPending) return null;

  return (
    <div
      style={{
        borderTop: `1px solid ${color.border}`,
        paddingTop: space.sm,
      }}
    >
      <Stack gap={space.sm}>
        <span style={{ fontSize: '15px', fontWeight: 600 }}>Análise da IA</span>

        {isPending && <Muted style={{ fontSize: '14px' }}>Analisando…</Muted>}

        {erro && <Erro erro={erro} />}

        {/* "Ainda não conferido" NÃO é erro, é o portão da revisão funcionando —
            e a saída está a um clique dali, no botão "Conferir lado a lado" do
            mesmo card. Dizer isso é mais útil que um aviso genérico. */}
        {semDado && (
          <div
            style={{
              background: status.yellow.bg,
              border: `1px solid ${status.yellow.border}`,
              borderRadius: '8px',
              padding: space.sm,
              fontSize: '14px',
              color: status.yellow.fg,
            }}
          >
            {result?.motivo === 'aguardando_conferencia' ? (
              <>
                <strong>Este exame ainda tem valores não conferidos.</strong> A análise
                só usa valor que você já confirmou — confira lado a lado para liberá-la.
              </>
            ) : (
              <>Não há valor reconhecido neste exame para analisar.</>
            )}
          </div>
        )}

        {analise && <Conteudo registro={analise} onRefazer={onRefazer} />}
      </Stack>
    </div>
  );
}

/**
 * Cada código de erro pede uma ação diferente de quem está lendo.
 *
 * "Não autorizado pelo paciente" é a mais importante de não confundir com falha
 * técnica: não é bug, é o consentimento funcionando, e a saída é conversar com
 * o paciente — não abrir chamado.
 */
function Erro({ erro }: { erro: DocumentAnalysisError }) {
  const tom = erro.code === 'sem_consentimento' ? status.yellow : status.red;

  return (
    <div
      style={{
        background: tom.bg,
        border: `1px solid ${tom.border}`,
        borderRadius: '8px',
        padding: space.sm,
      }}
    >
      <span style={{ fontSize: '14px', color: tom.fg }}>
        {erro.code === 'sem_consentimento' && (
          <>
            <strong>O paciente ainda não autorizou a análise por IA.</strong> A
            autorização é dada por ele, no aplicativo, e pode ser revogada quando
            quiser.
          </>
        )}
        {erro.code === 'sem_chave' && (
          <>
            <strong>O serviço de análise não está configurado.</strong> Falta a chave
            da OpenAI nas variáveis da edge function.
          </>
        )}
        {erro.code !== 'sem_consentimento' && erro.code !== 'sem_chave' && erro.message}
      </span>
    </div>
  );
}

interface Direcao {
  rotulo: string;
  cor: string;
}

/**
 * `estavel` é o fallback: o enum do schema garante um dos quatro, mas se um
 * valor novo aparecer o card mostra "estável" em vez de quebrar a página do
 * médico. Degradar é melhor que sumir.
 */
const DIRECAO_PADRAO: Direcao = { rotulo: '→ estável', cor: color.textSecondary };

const DIRECAO: Record<string, Direcao> = {
  alterado: { rotulo: '● alterado', cor: status.yellow.fg },
  melhora: { rotulo: '↑ melhora', cor: status.green.fg },
  piora: { rotulo: '↓ piora', cor: status.red.fg },
  estavel: { rotulo: '→ estável', cor: color.textSecondary },
};

function Conteudo({
  registro,
  onRefazer,
}: {
  registro: StoredDocumentAnalysis;
  onRefazer: () => void;
}) {
  const a = registro.analysis as unknown as DocumentAnalysisContent;
  const brief = registro.brief as unknown as { analitos?: unknown[] } | null;
  const nAnalitos = Array.isArray(brief?.analitos) ? brief.analitos.length : 0;

  return (
    <Stack gap={space.md}>
      <p style={{ margin: 0, fontSize: '15px', lineHeight: 1.6 }}>{a.resumo}</p>

      {/* ALERTAS PRIMEIRO. É a única seção que o médico pode precisar ver antes
          de terminar de ler o resto — pô-la depois dos achados enterraria a
          informação mais urgente sob a mais detalhada. */}
      {a.alertas?.length > 0 && (
        <Stack gap={space.xs}>
          {a.alertas.map((item, i) => {
            const tom = item.severidade === 'alta' ? status.red : status.yellow;
            return (
              <div
                key={i}
                style={{
                  background: tom.bg,
                  border: `1px solid ${tom.border}`,
                  borderRadius: '8px',
                  padding: space.sm,
                  fontSize: '14px',
                  lineHeight: 1.5,
                  color: tom.fg,
                }}
              >
                <strong>{item.titulo}.</strong> {item.porque}
              </div>
            );
          })}
        </Stack>
      )}

      {a.achados?.length > 0 && (
        <Stack gap={space.sm}>
          {a.achados.map((achado, i) => {
            const dir = DIRECAO[achado.direcao] ?? DIRECAO_PADRAO;
            // Não verificado ganha borda de aviso. É o único caso em que a cor
            // fala de confiabilidade do TEXTO, não do estado clínico.
            const suspeito = achado.verificado === false;
            return (
              <div
                key={i}
                style={{
                  borderLeft: `3px solid ${suspeito ? status.yellow.border : color.border}`,
                  background: suspeito ? status.yellow.bg : 'transparent',
                  paddingLeft: space.sm,
                  paddingTop: '2px',
                  paddingBottom: '2px',
                }}
              >
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'baseline',
                    gap: space.sm,
                    flexWrap: 'wrap',
                  }}
                >
                  <span style={{ fontSize: '15px', fontWeight: 600 }}>{achado.titulo}</span>
                  <span style={{ fontSize: '13px', color: dir.cor }}>{dir.rotulo}</span>
                  <span style={{ fontSize: '12px', color: color.textSecondary }}>
                    confiança {achado.confianca}
                  </span>
                </div>
                <p
                  style={{
                    margin: '2px 0 0',
                    fontSize: '14px',
                    lineHeight: 1.5,
                    color: color.text,
                  }}
                >
                  {achado.observacao}
                </p>
                {suspeito && (
                  <p style={{ margin: '4px 0 0', fontSize: '12px', color: status.yellow.fg }}>
                    Este achado cita número que não foi encontrado no exame. Confira no
                    laudo antes de considerá-lo.
                  </p>
                )}
              </div>
            );
          })}
        </Stack>
      )}

      {a.correlacoes?.length > 0 && (
        <Stack gap={space.xs}>
          <span style={{ fontSize: '14px', fontWeight: 600 }}>Analitos que se movem juntos</span>
          {a.correlacoes.map((c, i) => (
            <div key={i} style={{ fontSize: '14px', lineHeight: 1.5 }}>
              {c.observacao}
            </div>
          ))}
        </Stack>
      )}

      {a.perguntas_sugeridas?.length > 0 && (
        <Stack gap={space.xs}>
          <span style={{ fontSize: '14px', fontWeight: 600 }}>Perguntas para a consulta</span>
          <ul style={{ margin: 0, paddingLeft: '1.1rem' }}>
            {a.perguntas_sugeridas.map((p, i) => (
              <li key={i} style={{ fontSize: '14px', lineHeight: 1.5 }}>
                {p}
              </li>
            ))}
          </ul>
        </Stack>
      )}

      <Muted style={{ fontSize: '12px' }}>{a.qualidade_do_dado}</Muted>

      {/* Procedência. Sem isto o texto vira laudo anônimo — e a data importa
          tanto quanto a origem. */}
      <div
        style={{
          borderTop: `1px solid ${color.border}`,
          paddingTop: space.xs,
          display: 'flex',
          alignItems: 'baseline',
          justifyContent: 'space-between',
          gap: space.sm,
          flexWrap: 'wrap',
        }}
      >
        <Muted style={{ fontSize: '12px' }}>
          Gerado por inteligência artificial sobre {nAnalitos}{' '}
          {nAnalitos === 1 ? 'valor conferido' : 'valores conferidos'} em{' '}
          {new Date(registro.generated_at).toLocaleString('pt-BR')}. Não é laudo nem
          diagnóstico.
        </Muted>
        <Button variant="quiet" onClick={onRefazer} style={{ padding: 0 }}>
          Analisar de novo
        </Button>
      </div>
    </Stack>
  );
}
